/**
 *
 */
export function useScrollManagement(): {
  updateScrollState: (tabsContainerElement: HTMLElement | null) => void;
  scrollToTab: (
    tabsContainerElement: HTMLElement | null,
    tabs: HTMLElement[],
    index: number,
  ) => void;
  scrollLeftButton: (tabsContainerElement: HTMLElement | null, tabs: HTMLElement[]) => void;
  scrollRightButton: (tabsContainerElement: HTMLElement | null, tabs: HTMLElement[]) => void;
  canScrollLeft: () => boolean;
  canScrollRight: () => boolean;
  scrollLeft: () => number;
  recalculateScrollPoints: (tabsContainerElement: HTMLElement | null, tabs: HTMLElement[]) => void;
} {
  let scrollLeft = $state(0);
  let canScrollLeft = $state(false);
  let canScrollRight = $state(false);
  let scrollPoints = $state<number[]>([0]);
  let containerWidth = $state(0);

  /**
 *
 *
 */
  function calculateScrollPoints(
    tabsContainerElement: HTMLElement | null,
    tabs: HTMLElement[],
  ): number[] {
    if (!tabsContainerElement || tabs.length === 0) return [0];

    const viewportWidth = tabsContainerElement.clientWidth;
    const gap = parseFloat(getComputedStyle(tabsContainerElement).gap) || 0;
    const scrollPositions: number[] = [0];

    let pos = 0; /*                  */
    let viewEnd = viewportWidth;
    let checkpoint = 0;

    tabs.forEach((tab) => {
      const width = tab.offsetWidth;

      /*                                                 */
      if (pos + width > viewEnd) {
        if (checkpoint > 0) {
          scrollPositions.push(checkpoint);
        }

        checkpoint = pos;
        viewEnd = pos + viewportWidth;
      }

      pos += width + gap;
    });

    if (checkpoint > 0) {
      scrollPositions.push(checkpoint);
    }

    return scrollPositions;
  }

  /**
 *
 */
  function getCurrentPage(): number {
    if (scrollLeft <= 0 || scrollPoints.length <= 1) return 0;

    /*                            */
    for (let i = scrollPoints.length - 1; i >= 0; i--) {
      if (scrollLeft >= scrollPoints[i] - 5) {
        return i;
      }
    }

    return 0;
  }

  function updateScrollState(tabsContainerElement: HTMLElement | null): void {
    if (!tabsContainerElement) return;

    const { scrollLeft: sl, scrollWidth, clientWidth } = tabsContainerElement;
    scrollLeft = sl;
    containerWidth = clientWidth;

    /*                                                                 */
    const currentPage = getCurrentPage();
    const hasMultiplePages = scrollPoints.length > 1;
    canScrollLeft = hasMultiplePages && currentPage > 0 && sl > 1;
    canScrollRight = sl < scrollWidth - clientWidth - 1;
  }

  function recalculateScrollPoints(
    tabsContainerElement: HTMLElement | null,
    tabs: HTMLElement[],
  ): void {
    scrollPoints = calculateScrollPoints(tabsContainerElement, tabs);
  }

  function scrollToTab(
    tabsContainerElement: HTMLElement | null,
    tabs: HTMLElement[],
    index: number,
  ): void {
    if (!tabsContainerElement || !tabs[index]) return;

    const tab = tabs[index];
    /*                                                                             */
    if (typeof tab.scrollIntoView === "function") {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        tab.scrollIntoView({ inline: "center", block: "nearest", behavior: "instant" });
        return;
      }

      tab.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    }
  }

  function scrollLeftButton(tabsContainerElement: HTMLElement | null, tabs: HTMLElement[]): void {
    if (!tabsContainerElement) return;

    /*                                             */
    recalculateScrollPoints(tabsContainerElement, tabs);

    const currentPage = getCurrentPage();
    const targetPage = Math.max(0, currentPage - 1);
    const targetScroll = scrollPoints[targetPage] ?? 0;

    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "instant"
      : "smooth";
    tabsContainerElement.scrollTo({ left: targetScroll, behavior });
  }

  function scrollRightButton(tabsContainerElement: HTMLElement | null, tabs: HTMLElement[]): void {
    if (!tabsContainerElement) return;

    /*                                             */
    recalculateScrollPoints(tabsContainerElement, tabs);

    const currentPage = getCurrentPage();
    const targetPage = Math.min(scrollPoints.length - 1, currentPage + 1);
    const targetScroll = scrollPoints[targetPage] ?? scrollPoints[scrollPoints.length - 1];

    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "instant"
      : "smooth";
    const maxScroll = tabsContainerElement.scrollWidth - containerWidth;
    tabsContainerElement.scrollTo({ left: Math.min(targetScroll, maxScroll), behavior });
  }

  return {
    scrollLeft: () => scrollLeft,
    canScrollLeft: () => canScrollLeft,
    canScrollRight: () => canScrollRight,
    updateScrollState,
    scrollToTab,
    scrollLeftButton,
    scrollRightButton,
    recalculateScrollPoints,
  };
}
